/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DEFAULT_CLUSTER_ALLOCATION_MODE } from '../constants/settings';
import { EntityId } from '../types/api/EntityId';
import { ClusterAllocationMode } from '../types/common/AllocationMode';
import { LoginMode } from '../types/common/LoginMode';

const USER_NOTIFICATIONS = 'USER_NOTIFICATIONS';

const serialize = (data: string | null) => {
  try {
    return JSON.stringify(data);
  } catch (e) {
    console.error(e);
    return '';
  }
};

const deserialize = (data: string | null) => {
  if (data === null) {
    return undefined;
  }
  try {
    return JSON.parse(data);
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const userNotifications = (() => {
  const getKey = (id: EntityId) => `${USER_NOTIFICATIONS}_${id}`;
  const get = (id: EntityId) => deserialize(localStorage.getItem(getKey(id)));
  const set = (id: EntityId, value: any) =>
    localStorage.setItem(getKey(id), typeof value === 'string' ? value : serialize(value));
  const remove = (id: EntityId) => localStorage.removeItem(getKey(id));
  return { get, set, remove };
})();

export const jwtToken = (() => {
  const key = 'jwt_token';
  const get = () => localStorage.getItem(key) || '';
  const set = (value: string) => localStorage.setItem(key, value);
  return { get, set };
})();

export const uiLanguage = (() => {
  const getKey = () => 'ui_language';
  const get = () => localStorage.getItem(getKey()) || '';
  const set = (value: string) => localStorage.setItem(getKey(), value);
  return {
    get,
    set,
  };
})();

export const allocationMode = (() => {
  const key = 'allocation_mode';

  const get = (): ClusterAllocationMode => {
    const mode = localStorage.getItem(key);
    return mode === ClusterAllocationMode.Plan || mode === ClusterAllocationMode.Dynamic
      ? mode
      : DEFAULT_CLUSTER_ALLOCATION_MODE;
  };
  const set = (value: ClusterAllocationMode) => localStorage.setItem(key, value);

  const visited = (): boolean => localStorage.getItem(key) !== null;

  return { get, set, visited };
})();

export const clusterUpgradeHandled = (() => {
  const key = 'cluster_upgrade_handled';

  const getKey = (id: EntityId) => `${key}_${id}`;
  const get = (id: EntityId): boolean => {
    const handledUpgrades = localStorage.getItem(getKey(id)) || '';
    return handledUpgrades === 'true';
  };
  const set = (id: EntityId, value: boolean) => {
    localStorage.setItem(getKey(id), value.toString());
  };
  const remove = (id: EntityId) => localStorage.removeItem(getKey(id));

  return { get, set, remove };
})();

export const loginMode = (() => {
  const key = 'login_mode';

  const get = (): LoginMode => {
    const mode = localStorage.getItem(key);
    return mode === LoginMode.Password || mode === LoginMode.SSO ? mode : LoginMode.Select;
  };
  const set = (value: LoginMode) => localStorage.setItem(key, value);

  return { get, set };
})();
